import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup
} from '@mui/material';
import { CustomFieldDefinition, PermissionSetDomain } from '../../../types';
import { DEFAULT_ADVANCE_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { fetchCustomFieldDefinitionsApi } from '../../../Api/Admin/CustomFields/customFieldDefinitionsApi';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { getBuiltInFields } from './builtInFieldCatalog';
import { getDomainConfig } from './domains';

export interface FieldRestrictionsPickerProps {
  open: boolean;
  domain: PermissionSetDomain;
  accountId: string;
  hiddenFields: string[];
  onChange: (hiddenFields: string[]) => void;
  onClose: () => void;
}

interface FieldOption {
  key: string;
  label: string;
}

const toCustomOption = (definition: CustomFieldDefinition): FieldOption => ({
  key: definition.id,
  label: definition.inherited ? `${definition.name} (inherited)` : definition.name
});

export const FieldRestrictionsPicker = ({
  open,
  domain,
  accountId,
  hiddenFields,
  onChange,
  onClose
}: FieldRestrictionsPickerProps) => {
  const [customFields, setCustomFields] = useState<CustomFieldDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const entityType = getDomainConfig(domain)?.customFieldEntityType ?? null;

  useEffect(() => {
    setError(null);
    if (!open || !accountId || !entityType) {
      setCustomFields([]);
      return;
    }
    let active = true;
    setLoading(true);
    fetchCustomFieldDefinitionsApi(accountId, undefined, entityType).then((result) => {
      if (!active) return;
      setLoading(false);
      if (result.error) {
        setError(result.error.message || 'Could not load custom fields for this account.');
        return;
      }
      setCustomFields((result.data ?? []).filter((definition) => !definition.is_archived));
    });
    return () => {
      active = false;
    };
  }, [open, accountId, entityType]);

  const options = useMemo<FieldOption[]>(
    () => [
      ...getBuiltInFields(domain).map((field) => ({ key: field.field, label: field.label })),
      ...customFields.map(toCustomOption)
    ],
    [domain, customFields]
  );

  const allKeys = options.map((option) => option.key);
  const allHidden = allKeys.length > 0 && allKeys.every((key) => hiddenFields.includes(key));

  const toggleField = (key: string) =>
    onChange(hiddenFields.includes(key) ? hiddenFields.filter((k) => k !== key) : [...hiddenFields, key]);

  const toggleAll = () =>
    onChange(
      allHidden ? hiddenFields.filter((key) => !allKeys.includes(key)) : [...new Set([...hiddenFields, ...allKeys])]
    );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm">
      <DialogTitle>Field Restrictions — {getDomainConfig(domain)?.label}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert
            severity="error"
            className="mb-2">
            {error}
          </Alert>
        )}
        {loading && <ComponentSpinner label="Loading custom fields" />}
        <FormControlLabel
          control={
            <Checkbox
              checked={allHidden}
              onChange={toggleAll}
            />
          }
          label="Select all"
        />
        <Divider className="my-2" />
        <FormGroup>
          {options.map((option) => (
            <FormControlLabel
              key={option.key}
              control={
                <Checkbox
                  checked={hiddenFields.includes(option.key)}
                  onChange={() => toggleField(option.key)}
                />
              }
              label={option.label}
            />
          ))}
        </FormGroup>
      </DialogContent>
      <DialogActions>
        <button
          type="button"
          className={DEFAULT_ADVANCE_BUTTON_STYLE}
          onClick={onClose}>
          Done
        </button>
      </DialogActions>
    </Dialog>
  );
};
